/**
 * useRealtimeNotifications - Hook pour les notifications en temps réel via WebSocket
 * Affiche un toast quand une opération change de statut
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { buildAuthenticatedWsUrl } from '../utils/wsTicket';

// Labels des statuts en français
const STATUS_LABELS = {
  'demande_enregistree': 'Demande enregistrée',
  'en_attente_validation_client': 'En attente de validation',
  'devis_accepte': 'Devis accepté',
  'devis_refuse': 'Devis refusé',
  'planifiee': 'Planifiée',
  'prete_au_chargement': 'Prête au chargement',
  'en_cours_de_chargement': 'En cours de chargement',
  'charge_en_route': 'En route',
  'livree': 'Livrée',
  'en_attente_paiement': 'En attente de paiement',
  'terminee': 'Terminée',
  'annulee_admin': 'Annulée',
  'annulee_par_client': 'Annulée par le client',
  'cancelled': 'Annulée'
};

// Icônes pour les statuts
const STATUS_ICONS = {
  'demande_enregistree': '📋',
  'en_attente_validation_client': '⏳',
  'devis_accepte': '✅',
  'devis_refuse': '❌',
  'planifiee': '📅',
  'prete_au_chargement': '📦',
  'en_cours_de_chargement': '🏗️',
  'charge_en_route': '🚚',
  'livree': '🎉',
  'en_attente_paiement': '💰',
  'terminee': '✔️',
  'annulee_admin': '🚫',
  'annulee_par_client': '🚫',
  'cancelled': '🚫'
};

// SEC-003 — Auth WS via ticket court-vie (cf. utils/wsTicket.js)
const getWebSocketUrl = (path) => buildAuthenticatedWsUrl(path);

/**
 * Hook pour les notifications en temps réel (admin/staff)
 * Se connecte au WebSocket admin et affiche des toasts pour les changements de statut
 */
export const useRealtimeNotifications = (options = {}) => {
  const { 
    enabled = true, 
    showToasts = true,
    onStatusChange = null,
    filterOperations = null // Fonction pour filtrer les opérations (ex: par client_id)
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  const connectRef = useRef(null);

  useEffect(() => {
    const connect = async () => {
      if (!enabled) return;

      try {
        const wsUrl = await getWebSocketUrl('/ws/admin/tracking');
        if (!wsUrl) return; // pas authentifié
        logger.debug('Realtime notifications connecting to:', wsUrl);

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          logger.debug('Realtime notifications connected');
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            
            if (message.type === 'status_update') {
              const data = message.data;
              
              // Appliquer le filtre si fourni
              if (filterOperations && !filterOperations(data)) {
                return;
              }
              
              const notification = {
                id: `${data.operation_id}-${Date.now()}`,
                operationId: data.operation_id,
                operationNumber: data.details?.operationNumber || data.operation_id,
                oldStatus: data.details?.oldStatus,
                newStatus: data.status,
                clientName: data.details?.clientName,
                departure: data.details?.departure,
                arrival: data.details?.arrival,
                changedBy: data.details?.changedBy,
                timestamp: new Date(data.timestamp)
              };
              
              setLastNotification(notification);
              setNotifications(prev => [notification, ...prev].slice(0, 50)); // Garder les 50 dernières
              
              // Callback personnalisé
              if (onStatusChange) {
                onStatusChange(notification);
              }
              
              // Afficher un toast si activé
              if (showToasts) {
                const statusLabel = STATUS_LABELS[data.status] || data.status;
                const statusIcon = STATUS_ICONS[data.status] || '📌';
                
                toast.info(
                  `${statusIcon} ${notification.operationNumber}`,
                  {
                    description: `${statusLabel}${notification.clientName ? ` • ${notification.clientName}` : ''}`,
                    duration: 5000,
                    action: notification.operationId ? {
                      label: 'Voir',
                      onClick: () => {
                        window.location.href = `/operations/${notification.operationId}`;
                      }
                    } : undefined
                  }
                );
              }
            }
          } catch (e) {
            console.error('Error parsing realtime notification:', e);
          }
        };
        
        ws.onerror = (e) => {
          console.error('Realtime notification WebSocket error:', e);
        };
        
        ws.onclose = (e) => {
          logger.debug('Realtime notification WebSocket closed:', e.code);
          setIsConnected(false);
          
          // Reconnexion automatique avec backoff exponentiel
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            logger.debug(`Realtime notifications reconnecting in ${delay}ms...`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              if (connectRef.current) connectRef.current();
            }, delay);
          }
        };
        
      } catch (e) {
        console.error('Error creating realtime notification WebSocket:', e);
      }
    };

    connectRef.current = connect;

    if (enabled) {
      connect();
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [enabled, showToasts, onStatusChange, filterOperations]);

  // Heartbeat pour maintenir la connexion
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;
    
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(heartbeat);
  }, [isConnected]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    isConnected,
    lastNotification,
    notifications,
    clearNotifications,
    STATUS_LABELS,
    STATUS_ICONS
  };
};

export default useRealtimeNotifications;
